/**
 * CandidatoCard.tsx — Tarjeta de infraestructura con mapa inline expandible
 *
 * UID-03b | Fase 3 | feat/step3-coteja
 * @date 2026-03-06
 */

import { useState, type FC, type CSSProperties } from 'react';
import { colors, typography, borderRadius } from '@/design-system/v2';
import { InfrastructureType } from '@/types/infrastructure';
import { GlossaryTooltip } from '@/components/ui/GlossaryTooltip';
import type { CotejoItem } from './cotejoItem';
import { URGENCIA_CONFIG } from './types';
import { buildCatastroHref } from '@/utils/catastroUrl';
import { useCatastroStatus, CATASTRO_PARADAS_URL } from '@/utils/catastroAvailability';
import {
  getReferenciaNormativa,
  DISCLAIMER_NORMATIVO,
} from '@/services/cotejo/CriticidadNormativa';

// ─── Iconos tipología (DS v2: stroke currentColor 1.5) ───────────────────────

const TIPO_ICONS: Partial<Record<InfrastructureType, string>> = {
  [InfrastructureType.HEALTH]:     'M12 4v16m-8-8h16',
  [InfrastructureType.EDUCATION]:  'M4 19V9l8-5 8 5v10M9 19v-5h6v5',
  [InfrastructureType.POLICE]:     'M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z',
  [InfrastructureType.FIRE]:       'M12 2C6 8 4 13 4 16a8 8 0 0016 0c0-3-2-8-8-14z',
  [InfrastructureType.HYDRAULIC]:  'M12 2C8 8 6 12 6 16a6 6 0 0012 0c0-4-2-8-6-14z',
  [InfrastructureType.ENERGY]:     'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
  [InfrastructureType.MUNICIPAL]:  'M3 21V9l9-6 9 6v12M9 21v-6h6v6',
  [InfrastructureType.EMERGENCY]:  'M12 2v20M2 12h20',
  [InfrastructureType.CULTURAL]:   'M2 20h20M4 20V8l8-6 8 6v12',
};

const TIPO_COLORS: Partial<Record<InfrastructureType, string>> = {
  [InfrastructureType.HEALTH]:     colors.typologies.san.color,
  [InfrastructureType.EDUCATION]:  colors.typologies.edu.color,
  [InfrastructureType.POLICE]:     colors.typologies.pol.color,
  [InfrastructureType.FIRE]:       colors.typologies.eme.color,
  [InfrastructureType.HYDRAULIC]:  colors.typologies.hid.color,
  [InfrastructureType.ENERGY]:     colors.typologies.ene.color,
  [InfrastructureType.MUNICIPAL]:  colors.typologies.adm.color,
  [InfrastructureType.EMERGENCY]:  colors.typologies.eme.color,
  [InfrastructureType.CULTURAL]:   colors.typologies.cul.color,
};

const KIND_COLOR: Record<string, string> = {
  ausente:   colors.error,
  incluido:  colors.success,
  sobrante:  colors.info,
};

const KIND_LABEL: Record<string, string> = {
  ausente:  'Ausente',
  incluido: 'Incluido',
  sobrante: 'Sobrante',
};

/**
 * Término del glosario llano por estado del cotejo (A2b-i).
 * «incluido» no tiene término propio → el badge se muestra plano (sin tooltip),
 * evitando el `console.warn` de GlossaryTooltip para términos inexistentes.
 */
const KIND_TERM: Record<string, string | undefined> = {
  ausente:  'ausente',
  sobrante: 'sobrante',
};


// ─── Badge normativo (OL-01-UI) ──────────────────────────────────────────────

interface BadgeNormativoProps {
  tipologia: InfrastructureType;
  /** OL-02 — nombre del establecimiento para resolver subtipo INDUSTRIAL/FUEL */
  nombre?: string;
  /** OL-03 — Código catastral DGC 3 letras para máxima precisión normativa */
  destinoCat?: string;
}

/** Badge de referencia normativa: teal si alta, gris si media, null si baja. */
const BadgeNormativo: FC<BadgeNormativoProps> = ({ tipologia, nombre, destinoCat }) => {
  const ref = getReferenciaNormativa(tipologia, nombre, destinoCat);
  if (ref.confianza === 'baja') return null;

  const esAlta = ref.confianza === 'alta';
  const color  = esAlta ? '#2dd4bf' : '#71717a';
  const texto  = esAlta
    ? `${ref.norma} · ${ref.articulo}`
    : ref.norma;

  return (
    <span
      data-testid="badge-normativo"
      title={DISCLAIMER_NORMATIVO}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '1px 6px',
        borderRadius: borderRadius.sm,
        border: `1px solid ${color}40`,
        background: `${color}12`,
        color,
        fontSize: typography.fontSize.xs,
        fontFamily: typography.fontFamily.sans,
        cursor: 'help',
        flexShrink: 0,
      }}
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true">
        <path d="M12 3v18M5 8l7-5 7 5M5 16l7 5 7-5"/>
      </svg>
      {texto}
    </span>
  );
};

// ─── Subcomponente: icono de tipología ───────────────────────────────────────

const TipoIcon: FC<{ tipo: InfrastructureType; size?: number }> = ({ tipo, size = 16 }) => {
  const path = TIPO_ICONS[tipo] ?? 'M12 2a10 10 0 100 20A10 10 0 0012 2z';
  const color = TIPO_COLORS[tipo] ?? colors.fg.muted;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
};

// ─── Subcomponente: mapa placeholder (UTM30) ─────────────────────────────────

const MapaInline: FC<{ x?: number; y?: number; nombre: string }> = ({ x, y, nombre }) => {
  const frameStyle: CSSProperties = {
    marginTop: '10px',
    borderRadius: borderRadius.lg,
    overflow: 'hidden',
    border: `1px solid ${colors.border.DEFAULT}`,
    background: colors.bg.tertiary,
    height: '140px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexDirection: 'column', gap: '6px',
  };
  const coordStyle: CSSProperties = {
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.xs,
    color: colors.fg.muted,
  };
  return (
    <div style={frameStyle}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
        stroke={colors.fg.muted} strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
        <circle cx="12" cy="9" r="2.5"/>
      </svg>
      <span style={{ fontSize: typography.fontSize.xs, color: colors.fg.muted }}>{nombre}</span>
      {x && y && (
        <span style={coordStyle}>X {x.toLocaleString('es-ES')}  ·  Y {y.toLocaleString('es-ES')}</span>
      )}
    </div>
  );
};


// ─── Componente principal ─────────────────────────────────────────────────────

interface CandidatoCardProps {
  item: CotejoItem;
  onAnadir?: (id: string) => void;
  onIgnorar?: (id: string) => void;
}

export const CandidatoCard: FC<CandidatoCardProps> = ({ item, onAnadir, onIgnorar }) => {
  const [mapaOpen, setMapaOpen] = useState(false);
  const kindColor = KIND_COLOR[item.kind] ?? colors.fg.muted;
  const kindLabel = KIND_LABEL[item.kind] ?? item.kind;
  const kindTerm = KIND_TERM[item.kind];

  const cardStyle: CSSProperties = {
    background: colors.bg.secondary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.lg,
    padding: '14px 16px',
    marginBottom: '8px',
    fontFamily: typography.fontFamily.sans,
    transition: 'border-color 0.15s',
  };

  const headerStyle: CSSProperties = {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
  };

  const nameStyle: CSSProperties = {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.fg.primary,
    flex: 1,
  };

  const kindBadgeStyle: CSSProperties = {
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 8px', borderRadius: borderRadius.full,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    background: `${kindColor}18`,
    color: kindColor,
    border: `1px solid ${kindColor}40`,
    flexShrink: 0,
  };

  const metaRowStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '10px',
    marginTop: '8px', flexWrap: 'wrap',
  };

  const metaItemStyle: CSSProperties = {
    fontSize: typography.fontSize.xs,
    color: colors.fg.muted,
    display: 'flex', alignItems: 'center', gap: '4px',
  };

  // v7: coords+mapa izquierda · acciones derecha
  const actionsStyle: CSSProperties = {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px', marginTop: '12px',
  };

  const actionsLeftStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0,
  };

  const actionsRightStyle: CSSProperties = {
    display: 'flex', gap: '8px',
  };

  const coordsInlineStyle: CSSProperties = {
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.xs,
    color: colors.fg.muted,
  };

  const btnBaseStyle: CSSProperties = {
    flex: 1, padding: '6px 12px',
    borderRadius: borderRadius.DEFAULT,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.sans,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer', border: 'none',
    transition: 'all 0.15s',
  };

  const btnPrimaryStyle: CSSProperties = {
    ...btnBaseStyle,
    background: colors.primaryMuted,
    color: colors.primary,
    border: `1px solid ${colors.primary}40`,
  };

  const btnSecondaryStyle: CSSProperties = {
    ...btnBaseStyle,
    background: 'transparent',
    color: colors.fg.muted,
    border: `1px solid ${colors.border.DEFAULT}`,
  };

  const btnMapStyle: CSSProperties = {
    ...btnBaseStyle,
    flex: 'none',
    padding: '6px 10px',
    background: 'transparent',
    color: mapaOpen ? colors.primary : colors.fg.muted,
    border: `1px solid ${mapaOpen ? colors.primary + '40' : colors.border.DEFAULT}`,
  };

  /** Estilo enlace Catastro — ámbar, coherente con badge C3 de Step2 */
  const btnCatastroStyle: CSSProperties = {
    ...btnBaseStyle,
    flex: 'none',
    padding: '6px 10px',
    background: 'transparent',
    color: '#B45309',          // ámbar oscuro DS v2
    border: `1px solid #D9770640`,
    textDecoration: 'none',    // es un <a>, no un <button>
  };

  const urgCfg = item.urgencia ? URGENCIA_CONFIG[item.urgencia] : null;
  const catastroHref = buildCatastroHref({ refcat: item.refcat, x: item.x, y: item.y });

  const { status: catastroStatus } = useCatastroStatus();

  // Color del botón según estado (misma lógica que CatBadgeInline)
  const catastroColor = catastroStatus === 'down' ? colors.fg.muted : '#B45309';

  // Tooltip contextual
  const catastroTitle = (() => {
    if (!item.refcat && catastroStatus !== 'ok' && catastroStatus !== 'unknown') {
      return catastroStatus === 'maintenance'
        ? 'Posible parada programada — ver calendario de mantenimiento'
        : 'Sede no disponible — ver estado del servidor';
    }
    return item.refcat
      ? `Ficha catastral · ref. ${item.refcat}`
      : 'Ver parcela en Catastro (búsqueda por coordenadas)';
  })();

  // Si caído/mantenimiento sin refcat → redirigir a página de paradas
  const catastroHrefFinal = (
    (catastroStatus === 'down' || catastroStatus === 'maintenance') && !item.refcat
  ) ? CATASTRO_PARADAS_URL : (catastroHref ?? undefined);

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <TipoIcon tipo={item.tipologia} size={18} />
        <span style={nameStyle}>{item.nombre}</span>
        <span style={kindBadgeStyle}>
          {kindTerm
            ? <GlossaryTooltip term={kindTerm}>{kindLabel}</GlossaryTooltip>
            : kindLabel}
        </span>
      </div>

      <div style={metaRowStyle}>
        {urgCfg && (
          <span style={{ ...metaItemStyle, color: urgCfg.color }}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill={urgCfg.color}>
              <circle cx="5" cy="5" r="4"/>
            </svg>
            {urgCfg.label}
          </span>
        )}
        <BadgeNormativo tipologia={item.tipologia} nombre={item.nombre} destinoCat={item.destinoCat} />
      </div>

      {/* action-row v7: [coords · 🗺️] izquierda | [acciones] derecha */}
      <div style={actionsStyle}>

        {/* LEFT: coordenadas UTM30 + enlace Catastro + botón mapa */}
        <div style={actionsLeftStyle}>
          {item.x && item.y && (
            <span style={coordsInlineStyle} data-testid="coords-inline">
              {item.x.toLocaleString('es-ES')} · {item.y.toLocaleString('es-ES')}
            </span>
          )}
          {catastroHrefFinal && (
            <a
              href={catastroHrefFinal}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...btnCatastroStyle,
                color: catastroColor,
                border: `1px solid ${catastroColor}40`,
              }}
              title={catastroTitle}
              aria-label={catastroTitle}
              data-testid="btn-catastro"
            >
              🏛️{catastroStatus === 'maintenance' && (
                <svg
                  width="12" height="12" viewBox="0 0 16 16"
                  fill="none" xmlns="http://www.w3.org/2000/svg"
                  aria-label="Mantenimiento programado"
                  style={{ marginLeft: '3px', verticalAlign: 'middle', display: 'inline-block' }}
                >
                  <path d="M8 2L14.5 13.5H1.5L8 2Z"
                        stroke="#FB923C" strokeWidth="1.5"
                        strokeLinejoin="round" fill="none"/>
                  <line x1="8" y1="6.5" x2="8" y2="9.5"
                        stroke="#FB923C" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="8" cy="11.5" r="0.75" fill="#FB923C"/>
                </svg>
              )}
              {catastroStatus === 'down' && ' ✗'}
            </a>
          )}
          <button type="button" style={btnMapStyle}
            onClick={() => setMapaOpen(o => !o)}
            title={mapaOpen ? 'Cerrar mapa' : 'Ver en mapa'}
            data-testid="btn-mapa">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
              <circle cx="12" cy="9" r="2.5"/>
            </svg>
          </button>
        </div>

        {/* RIGHT: acciones principales */}
        <div style={actionsRightStyle}>
          {item.kind === 'ausente' && (
            <button type="button" style={btnPrimaryStyle}
              onClick={() => onAnadir?.(item.id)}>
              Añadir al PTEL
            </button>
          )}
          <button type="button" style={btnSecondaryStyle}
            onClick={() => onIgnorar?.(item.id)}>
            Ignorar
          </button>
        </div>

      </div>

      {mapaOpen && <MapaInline x={item.x} y={item.y} nombre={item.nombre} />}
    </div>
  );
};
